import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Table, Button, Spin, message, Dropdown, Menu, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { ObjectVersion } from "@aws-sdk/client-s3";
import type { IMsalContext } from "@azure/msal-react";
import type { IGetAgCredsResponse } from "components/ObjectList/objectListApi";
import { CombinedVersion, getObjectVersionsPage, getObjectVersionDownloadUrl } from "./inlineVersionsApi";
import { timeAgo } from "components/ObjectList";
import { DownloadOutlined, MoreOutlined, DeleteOutlined } from "@ant-design/icons";
import { DeleteMarkerModal } from "components/Delete";
import { deleteDeleteMarkerAPI } from "components/Delete/deleteApi";
import "./style.scss";

const SCROLL_Y_PX = 200;

export interface InlineVersionsTableProps {
  bucket: string;
  objectKey: string;     // ✅ renamed: was "key" (React reserved)
  name: string;          // for download filename
  msalInstance: IMsalContext;
  region: string;
  agCredsResponseData: IGetAgCredsResponse | boolean;
  pageSize?: number;     // default 10
  initialVersions?: CombinedVersion[];
  initialIsTruncated?: boolean;
  initialNextKeyMarker?: string;
  initialNextVersionIdMarker?: string;
  active?: boolean;
  /** Blocks version downloads when the current object is quarantined, infected or unverified. */
  restricted?: boolean;
  // GxP delete-marker removal props
  isGxpSpace?: boolean;
  domainName?: string;
  spaceName?: string;
  /** Called after a delete-marker removal request is successfully submitted */
  onDeleteMarkerSubmitted?: () => void;
}

const formatBytes = (bytesStr: string): string => {
  const bytes = parseInt(bytesStr, 10);
  if (isNaN(bytes)) return bytesStr;
  if (bytes === 0) return "-";
  const k = 1024, sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const InlineVersionsTable: React.FC<InlineVersionsTableProps> = ({
  bucket,
  objectKey,
  name,
  msalInstance,
  region,
  agCredsResponseData,
  pageSize = 10,
  initialVersions,
  initialIsTruncated,
  initialNextKeyMarker,
  initialNextVersionIdMarker,
  active = true,
  restricted = false,
  isGxpSpace = false,
  domainName = "",
  spaceName = "",
  onDeleteMarkerSubmitted,
}) => {
  const [versions, setVersions] = useState<CombinedVersion[]>(initialVersions ?? []);
  const [loading, setLoading] = useState<boolean>(!initialVersions);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [isTruncated, setIsTruncated] = useState<boolean>(!!initialIsTruncated);
  const [nextKeyMarker, setNextKeyMarker] = useState<string | undefined>(initialNextKeyMarker);
  const [nextVersionIdMarker, setNextVersionIdMarker] = useState<string | undefined>(initialNextVersionIdMarker);

  // Delete-marker modal state
  const [deleteMarkerModalOpen, setDeleteMarkerModalOpen] = useState(false);
  const [deleteMarkerLoading, setDeleteMarkerLoading] = useState(false);
  const [pendingDeleteMarker, setPendingDeleteMarker] = useState<CombinedVersion | null>(null);

  // Initial load only when active (expanded), with abort on collapse
  const abortRef = React.useRef<AbortController | null>(null);
  useEffect(() => {
    if (!active) return; // don’t fetch when collapsed

    let cancelled = false;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    const load = async () => {
      if (initialVersions) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const page = await getObjectVersionsPage(
          bucket, objectKey, msalInstance, region, agCredsResponseData,
          { maxKeys: pageSize },
        );
        if (cancelled) return;
        setVersions(page.versions);
        setIsTruncated(page.isTruncated);
        setNextKeyMarker(page.nextKeyMarker);
        setNextVersionIdMarker(page.nextVersionIdMarker);
      } catch (e: any) {
        if (e?.name === "AbortError") return;
        if (!cancelled) {
          message.error("Failed to load versions");
          setVersions([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [active, bucket, objectKey, msalInstance, region, agCredsResponseData, pageSize, initialVersions]);

  const handleDownload = useCallback(
    async (versionId: string) => {
      try {
        const url = await getObjectVersionDownloadUrl(
          bucket, objectKey, versionId, msalInstance, region, agCredsResponseData,
        );
        const a = document.createElement("a");
        a.href = url;
        a.download = name || "file";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch {
        message.error("Failed to generate download link");
      }
    },
    [bucket, objectKey, name, msalInstance, region, agCredsResponseData],
  );

  /** Open the delete-marker modal for the selected row */
  const handleOpenDeleteMarkerModal = useCallback((rec: CombinedVersion) => {
    setPendingDeleteMarker(rec);
    setDeleteMarkerModalOpen(true);
  }, []);

  /** Called when the user confirms the reason inside the DeleteMarkerModal */
  const handleDeleteMarkerSubmit = useCallback(async (reason: string) => {
    if (!pendingDeleteMarker) return;
    const versionId = "VersionId" in pendingDeleteMarker ? (pendingDeleteMarker.VersionId ?? "") : "";
    const fileKey = ("Key" in pendingDeleteMarker ? pendingDeleteMarker.Key : null) ?? objectKey;

    if (!versionId) {
      message.error("Cannot determine version ID for this delete marker.");
      return;
    }

    setDeleteMarkerLoading(true);
    try {
      await deleteDeleteMarkerAPI({
        bucket_name: bucket,
        region_name: region,
        space_name: spaceName,
        domain_name: domainName,
        file_key: fileKey,
        version_id: versionId,
        reason_for_deletion: reason,
        msalInstance,
      });
      message.success(
        "Delete marker removal request submitted. A co-owner will receive an email to approve or reject.",
      );
      setDeleteMarkerModalOpen(false);
      setPendingDeleteMarker(null);
      onDeleteMarkerSubmitted?.();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || "Unknown error";
      message.error(`Failed to submit delete marker removal: ${detail}`);
    } finally {
      setDeleteMarkerLoading(false);
    }
  }, [pendingDeleteMarker, bucket, region, spaceName, domainName, objectKey, msalInstance, onDeleteMarkerSubmitted]);

  const loadMore = useCallback(async () => {
    if (!isTruncated) return;
    setLoadingMore(true);
    try {
      const page = await getObjectVersionsPage(
        bucket, objectKey, msalInstance, region, agCredsResponseData,
        { keyMarker: nextKeyMarker, versionIdMarker: nextVersionIdMarker, maxKeys: pageSize },
      );
      setVersions(prev => [...prev, ...page.versions]);
      setIsTruncated(page.isTruncated);
      setNextKeyMarker(page.nextKeyMarker);
      setNextVersionIdMarker(page.nextVersionIdMarker);
    } finally {
      setLoadingMore(false);
    }
  }, [
    isTruncated, bucket, objectKey, msalInstance, region, agCredsResponseData,
    nextKeyMarker, nextVersionIdMarker, pageSize,
  ]);

  const columns: ColumnsType<CombinedVersion> = useMemo(() => ([    {
      title: "Version ID",
      dataIndex: "VersionId",
      key: "versionId",
      ellipsis: true,
      render: (val: any, rec: CombinedVersion) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <span
            title={val || "-"}
            style={{ maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {val || "-"}
          </span>
          {rec.__type === "DeleteMarker" && (
            <Tooltip
              title="This version has been soft deleted"
              overlayStyle={{ maxWidth: "300px", width: "300px" }}
              overlayInnerStyle={{ whiteSpace: "normal", textAlign: "center" }}
            >
              <span
                style={{
                  fontSize: 12,
                  color: "#cf1322",
                  border: "1px solid #ffd6e7",
                  padding: "0 6px",
                  borderRadius: 4,
                  background: "#fff1f0",
                }}
              >
                Delete marker
              </span>
            </Tooltip>
          )}

          {rec.__type !== "DeleteMarker" && (rec as any)?.IsLatest && (
            <Tooltip
              title="this is the latest version of the object."
              overlayStyle={{ maxWidth: 300, width: 300 }}
              overlayInnerStyle={{ whiteSpace: "normal", textAlign: "center" }}
            >
              <span
                style={{
                  fontSize: 12,
                  color: "#389e0d",
                  border: "1px solid #d9f7be",
                  padding: "0 6px",
                  borderRadius: 4,
                  background: "#f6ffed",
                }}
              >
                Latest Version
              </span>
            </Tooltip>
          )}
        </span>
      ),
      width: 360,
    },
    {
      title: "Last Modified",
      dataIndex: "LastModified",
      key: "lastModifiedAgo",
      render: (_: any, rec: CombinedVersion) => timeAgo(rec.LastModified as any),
      width: 150,
    },
    {
      title: "Region",
      key: "region",
      render: () => region || "-",
      width: 150,
    },
    {
      title: "Timestamp",
      key: "timestamp",
      render: (_: any, rec: CombinedVersion) =>
        rec.LastModified ? new Date(rec.LastModified as any).toLocaleString() : "-",
      width: 210,
    },
    {
      title: "Class",
      key: "class",
      render: (_: any, rec: CombinedVersion) =>
        rec.__type === "DeleteMarker"
          ? "-"
          : ((rec as ObjectVersion).StorageClass ?? "STANDARD"),
      width: 120,
    },
    {
      title: "Size",
      dataIndex: "Size",
      key: "size",
      render: (_: any, rec: CombinedVersion) =>
        rec.__type === "DeleteMarker"
          ? "-"
          : typeof (rec as ObjectVersion).Size === "number"
            ? formatBytes(String((rec as ObjectVersion).Size))
            : "-",
      width: 110,
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: 80,
      render: (_: any, rec: CombinedVersion) => {
        const versionId = "VersionId" in rec ? (rec.VersionId ?? "") : "";
        const isDeleteMarker = rec.__type === "DeleteMarker";
        const downloadDisabled = isDeleteMarker || !versionId || restricted;

        const menu = (
          <Menu className="action-menu-custom">
            <Menu.Item
              key="download"
              icon={<DownloadOutlined style={{ fontSize: 16 }} />}
              disabled={downloadDisabled}
              onClick={(info) => {
                info.domEvent?.stopPropagation?.();
                if (!downloadDisabled && versionId) handleDownload(versionId);
              }}
            >
              Download
            </Menu.Item>

            {/* "Remove Delete Marker" is only shown for GxP spaces on delete-marker rows */}
            {isGxpSpace && isDeleteMarker && (
              <Menu.Item
                key="remove-delete-marker"
                icon={<DeleteOutlined style={{ fontSize: 16, color: "#cf1322" }} />}
                onClick={(info) => {
                  info.domEvent?.stopPropagation?.();
                  handleOpenDeleteMarkerModal(rec);
                }}
                style={{ color: "#cf1322" }}
              >
                Remove Delete Marker
              </Menu.Item>
            )}
          </Menu>
        );

        return (
          <Dropdown
            overlay={menu}
            trigger={["hover"]}
            placement="bottomLeft"
            overlayClassName="action-menu-dropdown"
          >
            <span
              onClick={(e) => e.stopPropagation()}
              style={{
                cursor: "pointer",
                fontSize: 21,
                color: "#1677ff",
                padding: "6px 12px",
                borderRadius: 6,
                display: "inline-block",
                transition: "background 0.2s",
              }}
            >
              <MoreOutlined />
            </span>
          </Dropdown>
        );
      },
    },
  ]), [handleDownload, handleOpenDeleteMarkerModal, isGxpSpace, region, restricted]);



  if (loading) {
    return (
      <div style={{ padding: 12 }}>
        <Spin /> Loading versions…
      </div>
    );
  }

  return (
    <div style={{ marginLeft: "150px" }}>
      <Table<CombinedVersion>
        dataSource={versions}
        columns={columns}
        size="small"
        rowKey={(r) =>
          ("VersionId" in r && r.VersionId)
            ? `${r.__type}-${r.VersionId}`
            : `${r.__type}-${r.Key}-${r.LastModified}`
        }
        rowClassName={(record) =>
          record.__type === "DeleteMarker" ? "row-delete-marker" : "row-regular"
        }
        pagination={false}
        scroll={{ y: SCROLL_Y_PX, x: 1200 }}
        sticky
      />

      {isTruncated && (
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 8 }}>
          <Button type="primary" size="small" loading={loadingMore} onClick={loadMore}>
            {loadingMore ? "Loading…" : "View more"}
          </Button>
        </div>
      )}

      {/* Delete Marker removal modal — only rendered when a GxP delete-marker row action is triggered */}
      <DeleteMarkerModal
        visible={deleteMarkerModalOpen}
        fileKey={
          pendingDeleteMarker
            ? (("Key" in pendingDeleteMarker ? pendingDeleteMarker.Key : null) ?? objectKey)
            : objectKey
        }
        onOk={handleDeleteMarkerSubmit}
        onCancel={() => {
          setDeleteMarkerModalOpen(false);
          setPendingDeleteMarker(null);
        }}
        loading={deleteMarkerLoading}
      />
    </div>
  );
};

export default InlineVersionsTable;